import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FlaskConical, Package, Radar, AlertCircle } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Tag } from "@/components/Reveal";
import { IMG } from "@/data/images";

const PILLARS = [
  {
    icon: FlaskConical,
    n: "01",
    title: "Bio-formulated coatings",
    text: "A micro-thin, plant-derived coating in development, designed to slow respiration and moisture loss — with the goal of being edible, tasteless and leaving no harsh residues.",
    points: ["Plant-derived ingredients", "No synthetic preservatives in target formulation", "Designed to be tuned per produce type"],
  },
  {
    icon: Package,
    n: "02",
    title: "Smart packaging",
    text: "Breathability-focused packaging concepts that aim to balance the atmosphere around each product to slow ripening.",
    points: ["Gas-exchange concepts under study", "Condensation-control targets", "Formats planned for retail & food service"],
  },
  {
    icon: Radar,
    n: "03",
    title: "Cold-chain intelligence",
    text: "A planned telemetry layer through transport and storage, designed to flag freshness risks before they become losses.",
    points: ["Temperature & humidity tracking", "Early-warning alert concepts", "Data to feed ongoing R&D"],
  },
];

const FreshnessSlider = () => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);

  const update = (clientX) => {
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100)));
  };

  return (
    <div>
      <div
        ref={ref}
        data-testid="freshness-slider"
        className="relative aspect-[16/10] overflow-hidden select-none cursor-ew-resize border border-forest/10 shadow-xl shadow-forest/10 bg-white touch-none"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          update(e.clientX);
        }}
        onPointerMove={(e) => {
          if (e.buttons === 1) update(e.clientX);
        }}
      >
        <img src={IMG.asparagus} alt="Illustrative target visual of protected produce at day twelve" className="absolute inset-0 h-full w-full object-cover" draggable={false} />
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={IMG.asparagus}
            alt="Illustrative visual of untreated produce at day twelve"
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "grayscale(0.75) sepia(0.4) brightness(0.72) contrast(0.85)" }}
            draggable={false}
          />
        </div>
        <div className="absolute top-0 bottom-0 w-px bg-cream" style={{ left: `${pos}%` }}>
          <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-forest text-cream flex items-center justify-center font-mono text-[9px] tracking-widest shadow-lg">
            ⇔
          </span>
        </div>
        <span className="absolute top-4 left-4 bg-deep/80 text-cream font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-2">
          Untreated · Day 12
        </span>
        <span className="absolute top-4 right-4 bg-forest/90 text-mint font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-2">
          Fresh Guard target · Day 12
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          className="sr-only"
          aria-label="Compare illustrative untreated versus protected produce"
          data-testid="freshness-range-input"
        />
      </div>
      <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-fog">
        Illustrative target — not a measured result · Drag to compare · Performance in pilot validation
      </p>
    </div>
  );
};

export default function OurSolution() {
  return (
    <div data-testid="our-solution-page">
      <PageHero
        chapter="03"
        tag="Our Solution"
        testId="solution-hero"
        lines={[<>Extend the life of fresh.</>, <>Naturally.</>]}
        lead="Fresh Guard is developing a protective system that works with the biology of produce, not against it — with the goal of keeping vegetables and fruits at their peak for longer."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-24 sm:pb-32">
        <Reveal><Tag>Day 12 — an illustrative target</Tag></Reveal>
        <Reveal delay={0.1} className="mt-8">
          <FreshnessSlider />
        </Reveal>
      </section>

      <section className="bg-white border-y border-forest/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24 sm:py-32">
          <Reveal><Tag>Three pillars, one system</Tag></Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.1}>
                <div
                  data-testid={`pillar-card-${p.n}`}
                  className="border border-forest/10 bg-cream p-8 h-full flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-forest/10"
                >
                  <div className="flex items-center justify-between">
                    <p.icon className="h-6 w-6 text-emerald" />
                    <span className="font-mono text-xs text-amber">{p.n}</span>
                  </div>
                  <h3 className="mt-6 font-display font-bold text-2xl text-forest">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-moss flex-1">{p.text}</p>
                  <ul className="mt-6 space-y-2 border-t border-forest/10 pt-5">
                    {p.points.map((pt) => (
                      <li key={pt} className="text-sm text-moss flex items-center gap-2">
                        <span className="h-1 w-1 rounded-full bg-emerald shrink-0" />
                        {pt}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-24">
        <Reveal>
          <div className="border border-amber/40 bg-amber/10 p-8 sm:p-10 flex gap-5 items-start" data-testid="credibility-note">
            <AlertCircle className="h-6 w-6 text-amber shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-bold text-xl text-forest">A note on our claims</h3>
              <p className="mt-3 text-sm sm:text-base leading-relaxed text-moss max-w-3xl">
                Fresh Guard&rsquo;s technology is in the R&D and pilot-validation stage.
                Performance figures on this site are engineering targets and illustrative
                models, not measured commercial results, and remain pending independent
                verification. Safety evaluation is planned in line with Saudi Food and Drug
                Authority frameworks. We would rather under-promise and over-deliver —
                credibility is our most valuable ingredient.
              </p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="text-center mt-20">
          <h2 className="font-display font-extrabold tracking-tight leading-[1.1] text-3xl sm:text-4xl text-forest max-w-2xl mx-auto">
            Want to see the science under the surface?
          </h2>
          <Link
            to="/technology"
            data-testid="solution-cta-technology"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-forest text-cream px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] transition-colors hover:bg-emerald"
          >
            Inside the technology
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
